/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { Icon } from '@iconify/react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister, $getNearestNodeOfType } from '@lexical/utils';

import {
    INSERT_ORDERED_LIST_COMMAND,
    INSERT_UNORDERED_LIST_COMMAND,
    REMOVE_LIST_COMMAND,
    $isListNode,
    ListNode,
} from '@lexical/list';
import {
    $getSelection,
    $isRangeSelection,
    FORMAT_ELEMENT_COMMAND,
    FORMAT_TEXT_COMMAND,
    SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { useCallback, useEffect, useRef, useState } from 'react';

const LowPriority = 1;

function Divider() {
    return <div className="divider" />;
}

export default function ToolbarPlugin() {
    const [editor] = useLexicalComposerContext();
    const toolbarRef = useRef(null);
    const [isBold, setIsBold] = useState(false);
    const [isItalic, setIsItalic] = useState(false);
    const [isUnderline, setIsUnderline] = useState(false);
    const [isStrikethrough, setIsStrikethrough] = useState(false);
    const [isInOrderedList, setIsInOrderedList] = useState(false);
    const [isInUnorderedList, setIsInUnorderedList] = useState(false);

    const $updateToolbar = useCallback(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
            // Existing code...
            setIsBold(selection.hasFormat('bold'));
            setIsItalic(selection.hasFormat('italic'));
            setIsUnderline(selection.hasFormat('underline'));
            setIsStrikethrough(selection.hasFormat('strikethrough'));

            // Check if selection has a list parent
            const anchorNode = selection.anchor.getNode();
            const element =
                anchorNode.getKey() === 'root'
                    ? anchorNode
                    : anchorNode.getTopLevelElementOrThrow();
            if ($isListNode(element)) {
                const parentList = $getNearestNodeOfType(anchorNode, ListNode);
                const listType = parentList ? parentList.getListType() : null;
                setIsInOrderedList(listType === 'number');
                setIsInUnorderedList(listType === 'bullet');
            } else {
                setIsInOrderedList(false);
                setIsInUnorderedList(false);
            }
        }
    }, [editor]);
    useEffect(() => {
        return mergeRegister(
            editor.registerUpdateListener(({ editorState }) => {
                editorState.read(() => {
                    $updateToolbar();
                });
            }),
            editor.registerCommand(
                SELECTION_CHANGE_COMMAND,
                (_payload, _newEditor) => {
                    $updateToolbar();
                    return false;
                },
                LowPriority
            ),

        );
    }, [editor, $updateToolbar]);

    return (
        <div className="toolbar border-b" ref={toolbarRef}>

            <Divider />
            <div className="flex gap-1 items-center ml-2">
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
                    }}
                    className={'toolbar-item spaced '}
                    aria-label="Format Bold"
                    title="Bold"
                >
                    <Icon
                        icon="ooui:bold-b"
                        style={{ color: isBold ? 'var(--cty-text-primary)' : 'var(--cty-text-muted)' }}
                    />
                </button>
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
                    }}
                    className={'toolbar-item spaced '}
                    aria-label="Format Italics"
                    title="Italic"
                >
                    <Icon
                        icon="fa6-solid:italic"
                        style={{ color: isItalic ? 'var(--cty-text-primary)' : 'var(--cty-text-muted)' }}
                    />
                </button>
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline');
                    }}
                    className={'toolbar-item spaced '}
                    aria-label="Format Underline"
                    title="Underline"
                >
                    <Icon
                        icon="mingcute:underline-fill"
                        style={{ color: isUnderline ? 'var(--cty-text-primary)' : 'var(--cty-text-muted)' }}
                    />
                </button>
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough');
                    }}
                    className={'toolbar-item spaced '}
                    aria-label="Format Strikethrough"
                    title="Strikethrough"
                >
                    <Icon
                        icon="mingcute:strikethrough-fill"
                        style={{ color: isStrikethrough ? 'var(--cty-text-primary)' : 'var(--cty-text-muted)' }}
                    />
                </button>
            </div>
            <Divider />
            <div></div>
            <div className="flex gap-1 items-center ml-2">
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
                    }}
                    className="toolbar-item spaced"
                    aria-label="Left Align"
                    title="Left Align"
                >
                    <Icon
                        icon="fluent:text-align-left-20-regular"
                        style={{ width: '18px', height: '18px' }}
                    />
                </button>
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
                    }}
                    className="toolbar-item spaced"
                    aria-label="Center Align"
                    title="Center Align"
                >
                    <Icon
                        icon="fluent:text-align-center-16-regular"
                        style={{ width: '18px', height: '18px' }}
                    />
                </button>
                <button
                    onClick={() => {
                        editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'right');
                    }}
                    className="toolbar-item spaced"
                    aria-label="Right Align"
                    title="Right Align"
                >
                    <Icon
                        icon="fluent:text-align-right-20-regular"
                        style={{ width: '18px', height: '18px' }}
                    />
                </button>
            </div>{' '}
            <Divider />
            <button
                onClick={() => {
                    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'justify');
                }}
                className="toolbar-item"
                aria-label="Justify Align"
                title="Justify Align"
            >
                <Icon icon="uim:align-justify" />
            </button>
            <button
                onClick={() => {
                    if (isInList) {
                        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
                    } else {
                        editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
                    }
                }}
                className={'toolbar-item spaced'}
                aria-label="Insert Unordered List"
                title="Unordered List"
            >
                <Icon
                    icon="mdi:format-list-bulleted"
                    style={{ color: isInUnorderedList ? 'var(--cty-text-primary)' : 'var(--cty-text-muted)' }}
                />
            </button>
            <button
                onClick={() => {
                    if (isInList) {
                        editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
                    } else {
                        editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
                    }
                }}
                className={'toolbar-item spaced '}
                aria-label="Insert Ordered List"
                title="Ordered List"
            >
                <Icon
                    icon="mdi:format-list-numbered"
                    style={{ color: isInOrderedList ? 'var(--cty-text-primary)' : 'var(--cty-text-muted)' }}
                />
            </button>
            <Divider />
        </div>
    );
}
